import { useFormValidation } from '@mnd-apps/public/hooks/useFormValidation';
import { ReCaptchaProvider, useReCaptcha } from '@mnd-apps/public/lib/next-recaptcha-v3';
import { selectContentClickHandler, track } from '@mnd-apps/public/lib/tracker';
import { routes } from '@mnd-apps/public/routes';
import logger from '@mnd-apps/public/utils/logger';
import type { PageFragment } from '@mnd-apps/public/wp-generated/types';
import CardQuotation from '@mnd-frontend/ui/public/components/CardQuotation';
import Form, { type FormField } from '@mnd-frontend/ui/public/components/Form';
import {
  type FieldNames,
  FORM_FIELD,
  type FormDataTuple,
} from '@mnd-frontend/ui/public/components/Form/index.type';
import InputCountryAndPhone from '@mnd-frontend/ui/public/components/InputCountryAndPhone';
import Heading from '@mnd-frontend/ui/public/components/typography/Heading';
import Wysiwyg from '@mnd-frontend/ui/public/components/typography/Wysiwyg';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import type { TextColorDataType } from '@mnd-frontend/ui/public/types/color';
import { getTextColor } from '@mnd-frontend/ui/public/utils/maps/color';
import { mapLangToLocale } from '@mnd-frontend/ui/public/utils/maps/locale';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import {
  BREAKPOINTS,
  SPACING_2,
  SPACING_3,
  SPACING_5,
  SPACING_10,
} from '@mnd-frontend/ui/shared/tokens';
import { type FormEventHandler, memo, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isBackgroundDark } from '../../getModuleColors';
import HeroImage from './HeroImage';
import type {
  TrialErrorResponse,
  TrialErrorsResponse,
  TrialResponses,
  TrialSuccessResponse,
} from './types/response';
import { sanitizeByPattern } from './utils/forms';

const NAVBAR_HEIGHT = '5.5rem';

const Section = styled.section`
  position: relative;
  padding: clamp(5rem, 10vw, 10rem) ${SPACING_2};
  min-height: calc(100vh - ${NAVBAR_HEIGHT});
`;

const Content = styled(ContentMaxWidth)`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_5};

  @media (min-width: ${BREAKPOINTS.LG}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING_10};
  }
`;

const DescriptionContainer = styled.div<{ $darkBackground: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_3};
  color: ${({ $darkBackground }) => ($darkBackground ? '#fff' : '#181646')};
`;

const QuoteContainer = styled.div`
  display: block;
`;

const InlineLink = styled.a`
  font-weight: 600;
  text-decoration: underline;
`;

type Props = {
  data: NonNullable<PageFragment['hero']>['heroTrial'];
};

const Termsi18nText = memo(({ lang }: { lang: string }) => (
  <Trans
    i18nKey="form.termsAndConditions"
    components={{
      a: (
        <InlineLink
          href={routes.termsConditions(lang)}
          onClick={selectContentClickHandler('terms_and_conditions', 'terms')}
          target="_blank"
          rel="noopener noreferrer"
        />
      ),
    }}
  />
));

const FORM_TRACKING_ID = 'mnd-signup-form-base';

// #region Form Component
const HeroFormTrial = ({ data }: Props) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { formTitle, submitButtonText, newsletterOptIn, bgColor, formAppendix } = {
    formTitle: data?.form?.title,
    submitButtonText: data?.form?.submitButton ?? t('form.formCta'),
    newsletterOptIn: data?.form?.newsletterOptIn,
    formAppendix: data?.form?.appendix,
    bgColor: data?.bgColor,
  };
  const { executeRecaptcha } = useReCaptcha();
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const { showSuccessToast, showFailureToast } = useToasts();
  const darkBackground = isBackgroundDark(bgColor);

  const trackError = (error_code: string, error_message: string, form_field: string) => {
    track({
      event: 'error_form_submit',
      traits: {
        event_info: {
          form_id: FORM_TRACKING_ID,
          error_code,
          error_message,
          form_field,
          form_name: formTitle ?? '',
          navigation_selection: submitButtonText ?? t('form.formCta'),
          user_interaction: 'cta_click',
        },
      },
    });
  };
  // #region Form Fields Map
  const {
    fieldErrors,
    validFields,
    clearValidation,
    validateCheckbox,
    validateEmail,
    validatePassword,
    validatePresence,
    setFieldError,
    setValidField,
  } = useFormValidation();

  const formFields = [
    {
      name: FORM_FIELD.NAME,
      label: t('form.field.label.name.full'),
      placeholder: t('form.name'),
      type: 'text',
      required: true,
      validate(value) {
        validatePresence(FORM_FIELD.NAME, { value, isRequired: this.required });
      },
    },
    {
      name: FORM_FIELD.COMPANY,
      label: t('form.field.label.company'),
      placeholder: t('form.companyName'),
      type: 'text',
      required: true,
      validate(value) {
        validatePresence(FORM_FIELD.COMPANY, { value, isRequired: this.required });
      },
    },
    {
      name: FORM_FIELD.PHONE_CODE,
      label: '',
      type: 'custom',
      component: (
        <InputCountryAndPhone
          validateHookPhone={validatePresence}
          validateHookCountry={validatePresence}
          errors={fieldErrors}
          placeholders={{
            country: t('form.field.placeholder.countryPhoneCode'),
          }}
          label={t('form.field.label.phone')}
        />
      ),
    },
    {
      name: FORM_FIELD.EMAIL,
      label: t('form.field.label.work_email'),
      placeholder: t('form.email'),
      type: 'email',
      required: true,
      validate(value) {
        validateEmail({ value });
      },
    },
    {
      name: FORM_FIELD.PASSWORD,
      label: t('form.field.label.password'),
      placeholder: t('form.password'),
      type: 'password',
      required: true,
      validate(value) {
        validatePassword({ value });
      },
    },
    {
      name: FORM_FIELD.NEWSLETTER,
      label: newsletterOptIn ?? t('form.marketingConsent'),
      type: 'checkbox',
      checked: false,
      required: false,
    },
    {
      name: FORM_FIELD.ACCEPT,
      label: <Termsi18nText lang={lang} />,
      type: 'checkbox',
      checked: false,
      required: true,
      validate(value) {
        validatePresence(FORM_FIELD.ACCEPT, { value, isRequired: this.required });
      },
    },
  ] as FormField[];
  // #endregion

  const handleRecpatchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      logger.error('ReCAPTCHA not available');
      return;
    }
    const token = await executeRecaptcha('HeroTrial');
    return token;
  }, [executeRecaptcha]);

  const validateForm = async (formData: FormData) => {
    const formDataArray = [...formData] as Array<FormDataTuple>;
    // When the box is unchecked, the form field won't appear in formData.
    // Therefore validate required checkbox here.
    const findTermsField = (field: FormDataTuple) => field[0] === FORM_FIELD.ACCEPT;
    if (!formDataArray.some(findTermsField)) {
      validateCheckbox(FORM_FIELD.ACCEPT, {
        value: '',
        errorMessage: t('form.validation.acceptTerms.required'),
        isRequired: true,
      });
      return false;
    }

    return Object.values(validFields).every(Boolean);
  };

  const handleOnSubmit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // #region Sanitizate and validate
    for (const [fieldName, userInput] of formData) {
      if (typeof userInput === 'string' && fieldName !== FORM_FIELD.ACCEPT) {
        let sanitizedInput: string;
        if (fieldName === FORM_FIELD.PHONE || fieldName === FORM_FIELD.NAME) {
          sanitizedInput = sanitizeByPattern(fieldName, userInput);
        } else {
          sanitizedInput = userInput.trim();
        }
        formData.delete(fieldName);
        formData.append(fieldName, sanitizedInput);
      }
    }

    const validForm = await validateForm(formData);
    if (!validForm) return;

    setSubmissionLoading(true);
    clearValidation();
    // #endregion

    const token = await handleRecpatchaVerify();

    try {
      const payload = Object.fromEntries([...formData]);
      const locale = mapLangToLocale(lang);

      const response = await fetch('/api/trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          locale,
          formData: payload,
        }),
      });

      if (response.ok) {
        const res: TrialResponses = await response.json();

        track({
          event: 'lead_generation',
          traits: {
            event_info: {
              form_id: FORM_TRACKING_ID,
              lead_source: '',
              navigation_selection: submitButtonText,
              form_name: formTitle || FORM_TRACKING_ID,
              user_interaction: 'lead_generation',
            },
          },
        });

        const successResponse = res as TrialSuccessResponse;

        const message = successResponse.data?.message_locale_key || 'common_thank_you';

        showSuccessToast(t(message));
        setSubmissionCompleted(true);

        if (successResponse.data?.redirect_url) {
          window.location.href = successResponse.data.redirect_url;
        }
      } else {
        const res: TrialErrorsResponse | TrialErrorResponse = await response.json();

        if (!res.success) {
          if (Object.hasOwn(res, 'errors')) {
            const response = res as TrialErrorsResponse;

            Object.entries(response.errors).forEach(([key, messages]) => {
              setValidField(key as FieldNames, false);

              messages.forEach(msg => {
                setFieldError(key as FieldNames, msg);
                trackError(msg, msg, key);
              });
            });

            showFailureToast(t('form.validation.fields.errors'));
            return;
          }

          const response = res as TrialErrorResponse;

          if (response.error.message_locale_key) {
            showFailureToast(t(response.error.message_locale_key));
          }

          if (response.error.type === 'form') {
            trackError(response.error.field, response.message, response.error.field);
            setFieldError(response.error.field as FieldNames, response.message);
          }
        } else {
          throw new Error('Unknown error');
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        // General unknown error
        logger.error({ error: e }, e.message || 'Something went wrong');
      }
    } finally {
      setSubmissionLoading(false);
    }
  };

  const textColor = getTextColor(data?.textColor?.at(0) as TextColorDataType);
  const quote = data?.quotes?.[0];
  const quotationCard = quote && (
    <CardQuotation
      quote={quote?.quote}
      name={quote?.name}
      jobTitle={quote?.jobTitle}
      profileImage={quote?.image}
      colorVariant={textColor === '#FFF' ? 'pink' : 'purple'}
    />
  );

  return (
    <Section>
      <HeroImage data={data} />
      <Content>
        <DescriptionContainer $darkBackground={darkBackground}>
          <Heading as="h1" color={textColor}>
            {data?.title}
          </Heading>
          <Wysiwyg color="inherit" htmlContent={data?.description} />
          {quote && <QuoteContainer>{quotationCard}</QuoteContainer>}
        </DescriptionContainer>
        <Form
          title={formTitle ?? undefined}
          submitButtonText={submitButtonText}
          fields={formFields}
          errors={fieldErrors}
          onSubmit={handleOnSubmit}
          loading={submissionLoading}
          disabled={submissionCompleted}
          appendix={formAppendix}
        />
      </Content>
    </Section>
  );
};

const HeroTrialWrapper = (props: Props) => {
  return (
    <ReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
      async={false}
      defer={false}
      className="optanon-category-C0001"
    >
      <HeroFormTrial {...props} />
    </ReCaptchaProvider>
  );
};

export default HeroTrialWrapper;
